/**
 * Abbreviates a number into K (thousands), M (millions), B (billions), T (trillions).
 * Examples:
 *   950       → "950"
 *   1000      → "1K"
 *   15300     → "15.3K"
 *   1200000   → "1.2M"
 *   530000000 → "530M"
 *   2000000000→ "2B"
 */
export const abbreviateNumber = (value: number): string => {
  const abs = Math.abs(value);

  if (abs >= 1e12) {
    const trillions = value / 1e12;
    return `${trimDecimal(trillions)}T`;
  } else if (abs >= 1e9) {
    const billions = value / 1e9;
    return `${trimDecimal(billions)}B`;
  } else if (abs >= 1e6) {
    const millions = value / 1e6;
    return `${trimDecimal(millions)}M`;
  } else if (abs >= 1e3) {
    const thousands = value / 1e3;
    return `${trimDecimal(thousands)}K`;
  } else {
    return String(value);
  }
};

/**
 * Helper: keeps one decimal place only if needed, but does NOT round—
 * it truncates to a single decimal.
 *
 * - 15.0  → "15"
 * - 15.3  → "15.3"
 * - 15.39 → "15.3"  (instead of "15.4")
 * - -2.78 → "-2.7"
 */
const trimDecimal = (num: number): string => {
  // Multiply by 10 to shift the decimal, then truncate
  const truncated = num >= 0 ? Math.floor(num * 10) / 10 : Math.ceil(num * 10) / 10;

  // If the result is an integer (e.g. 15.0), drop the ".0"
  return Number.isInteger(truncated) ? truncated.toString() : truncated.toString();
};
